import { useEffect } from 'react'
import ReportSection from 'ui/clinicianScreens/Patient/Report/components/ReportSection'
import {
  formatAverage,
  formatDiff,
} from 'ui/clinicianScreens/Patient/Report/helpers'
import {
  defaultAverage,
  getAverageDiff,
} from 'ui/clinicianScreens/Patient/Report/sections/TremorAndDyskinesia/helpers'
import {
  SECTION_TITLES,
  VIDEO_LINKS,
} from 'ui/clinicianScreens/Patient/Report/consts'
import ReportChartTremor from 'ui/clinicianScreens/Patient/Report/sections/TremorAndDyskinesia/Tremor/ReportChartTremor'
import { useSectionTitle } from 'ui/clinicianScreens/Patient/Report/sectionTitles'
import { Link, Text } from 'ui/baseComponents'
import { useReportTranslation } from 'utilities/i18n'

const TremorHelper = ({ t }) => (
  <Text variant="body14" component="p" data-cy="tremor-helper">
    {t('report.tremor.chartBarInfo')} {t('report.tremor.understandDifference')}{' '}
    <Link href={VIDEO_LINKS.tremorAndDyskinesia} external underline light>
      {t('report.common.watchOurVideo')}
    </Link>
    .
  </Text>
)

const TremorEmptyStateHelper = ({ t }) => (
  <Text variant="body14" component="p" data-cy="tremor-helper-no-data">
    {t('report.tremor.emptyStateBefore')}{' '}
    <Link
      href={VIDEO_LINKS.howToSetUpAndPairYourAppleWatch}
      external
      underline
      light
    >
      {t('report.tremor.followSteps')}
    </Link>{' '}
    {t('report.tremor.emptyStateMiddle')}{' '}
    {t('report.tremor.understandDifference')}{' '}
    <Link href={VIDEO_LINKS.tremorAndDyskinesia} external underline light>
      {t('report.common.watchOurVideo')}
    </Link>
    .
  </Text>
)

const ReportSectionTremor = ({
  onLoadStart = () => {},
  onLoadEnd = () => {},
  hiddenSections,
  addToHasDataSet,
  removeFromHasDataSet,
  isClinicianReport,
  hasDataSet,
  data,
  prevData,
  loading,
}) => {
  const title = SECTION_TITLES.TREMOR
  const sectionTitle = useSectionTitle()
  const { t } = useReportTranslation()

  useEffect(() => {
    if (loading) {
      onLoadStart(title)
    } else {
      onLoadEnd(title)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  let tremorAverage = { ...defaultAverage }

  let prevTremorAverage = { ...defaultAverage }

  if (data) {
    tremorAverage = formatAverage(data)
  }

  if (prevData) {
    prevTremorAverage = formatAverage(prevData)
  }

  const tremorAverageDiff = getAverageDiff(tremorAverage, prevTremorAverage)
  const hasTremorData = data?.numberOfDaysWithData > 0

  useEffect(() => {
    hasTremorData ? addToHasDataSet(title) : removeFromHasDataSet(title)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTremorData])

  return (
    <>
      <ReportSection
        {...{
          hidden:
            hiddenSections.has(title) ||
            (isClinicianReport && !hasDataSet.has(title)),
          noBorder: true,
        }}
      >
        <ReportChartTremor
          {...{
            currentData: data,
            prevData,
            onLoadStart,
            onLoadEnd,
            diff: formatDiff(t, tremorAverageDiff),
            displayTitle: sectionTitle(title),
          }}
        />
        {!isClinicianReport &&
          (hasTremorData ? (
            <TremorHelper t={t} />
          ) : (
            <TremorEmptyStateHelper t={t} />
          ))}
        {isClinicianReport && hasTremorData && (
          <Text variant="body14" component="p" data-cy="tremor-helper">
            {t('report.tremor.chartBarInfo')}
          </Text>
        )}
      </ReportSection>
    </>
  )
}

export default ReportSectionTremor
